import "altcha";
import "altcha/i18n/ja";
import "altcha/types/react";

import type { AltchaWidgetElement } from "altcha";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AltchaWidgetView } from "@/features/post/components/AltchaWidgetView";
import {
  ALTCHA_STATUS_MESSAGES,
  type AltchaAutoVerifier,
  type AltchaState,
  createAltchaAutoVerifier,
  createAltchaWidgetConfiguration,
  detectCoarsePointer,
  registerAltchaFocusTrigger,
  registerAltchaWidgetListeners,
  resolveAltchaWorkerConcurrency,
} from "@/features/post/components/altchaWidgetBehavior";
import {
  type AltchaCredential,
  type AltchaOperation,
  createAltchaChallengeController,
  getAltchaChallengePath,
  resolveAltchaCredential,
} from "@/features/post/lib/altchaChallengeController";
import { API_BASE, getAimgToken } from "@/shared/api/client";

type AltchaEventDetail = {
  payload: string | null;
  state: AltchaState | "code";
};

type AltchaVerifiedDetail = {
  payload: string;
};

export type AltchaWidgetHandle = {
  getCredential: (signal?: AbortSignal) => Promise<AltchaCredential>;
  reset: () => void;
};

type Props = {
  active?: boolean;
  hideFooter?: boolean;
  operation: AltchaOperation;
  tokenProvider?: () => Promise<string>;
};

const noop = (): void => undefined;

export const AltchaWidget: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<AltchaWidgetHandle>
> = forwardRef<AltchaWidgetHandle, Props>(function AltchaWidget(
  {
    active = true,
    hideFooter = false,
    operation,
    tokenProvider = getAimgToken,
  }: Props,
  ref,
): React.ReactElement | null {
  const labelId = useId();
  const descriptionId = useId();
  const widgetRef = useRef<AltchaWidgetElement | null>(null);
  const payloadRef = useRef<string | null>(null);
  const cleanupWidgetRef = useRef<() => void>(noop);
  const retryWhenReadyRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;
  const hideFooterRef = useRef(hideFooter);
  hideFooterRef.current = hideFooter;
  const configuredHideFooterRef = useRef(hideFooter);
  // 現在の verifier を SP の active 遷移から叩けるように保持する。
  const autoVerifierRef = useRef<AltchaAutoVerifier | null>(null);
  const [hasActivated, setHasActivated] = useState(active);
  const shouldRenderWidget = active || hasActivated;
  if (active && !hasActivated) {
    setHasActivated(true);
  }
  const [controller] = useState(() =>
    createAltchaChallengeController({ getToken: tokenProvider }),
  );
  const [state, setState] = useState<AltchaState>("unverified");
  const [widgetGeneration, setWidgetGeneration] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void tokenProvider().catch(() => {
      /* token 取得失敗は送信時に個別に扱う */
    });
    return (): void => {
      cancelled = true;
      void cancelled;
    };
    // Tokenの再発行はフォームを開いた時だけ行う。受動的な本人用一覧取得が
    // tokenを無効化しても、開いたフォームから即時再発行すると一覧再取得との
    // 間で発行・破棄ループになる。送信時のgetCredentialは必要なら別途再発行する。
  }, [active, tokenProvider]);

  // SP はモーダル/パネルの active が false→true になった瞬間に verify を起動する。
  // iOS のソフトキーボード表示アニメーションと PoW ワーカー起動の衝突を避けるため、
  // form の focusin ではなく open 契機を採る。PC (fine pointer) は focusin を維持。
  useEffect(() => {
    if (!active) return;
    if (!detectCoarsePointer()) return;
    autoVerifierRef.current?.request();
  }, [active]);

  // 表示モードの切り替えでは同じwidgetと認証payloadを維持し、見た目の設定だけ
  // 更新する。hideFooterをattachWidgetの依存にするとref cleanupが走り、認証済み
  // payloadとchallenge controllerまで破棄されてしまう。
  useEffect(() => {
    if (configuredHideFooterRef.current === hideFooter) return;
    configuredHideFooterRef.current = hideFooter;
    const widget = widgetRef.current;
    if (!widget) return;
    // 型定義はPromiseを返すが、custom elementのruntime実装は同期的に
    // undefinedを返す場合がある。async境界で両方を正規化する。
    void (async (): Promise<void> => {
      await widget.configure({ hideFooter });
    })().catch(() => {
      if (widgetRef.current === widget) setState("error");
    });
  }, [hideFooter]);

  function reset(): void {
    // altcha@3.2.1 can emit a late `error` after reset aborts challenge fetch.
    // Detach the old host first, then mount a fresh host so the aborted
    // verification cannot overwrite the next form lifecycle's state.
    cleanupWidgetRef.current();
    cleanupWidgetRef.current = noop;
    retryWhenReadyRef.current = false;
    payloadRef.current = null;
    controller.reset();
    setState("unverified");
    setWidgetGeneration((generation) => generation + 1);
  }

  async function getCredential(
    signal?: AbortSignal,
  ): Promise<AltchaCredential> {
    signal?.throwIfAborted();
    const widget = widgetRef.current;
    if (!widget) {
      throw new Error("書き込み認証を読み込んでいます。もう一度お試しください");
    }
    try {
      return await resolveAltchaCredential({
        freshPayload:
          widget.getState() === "verified" ? payloadRef.current : null,
        getFreshCredential: controller.getCredential,
        signal,
        verify: async () => {
          const result = await widget.verify();
          if (!result?.payload) {
            throw new Error("書き込み認証を完了できませんでした");
          }
          payloadRef.current = result.payload;
          return result.payload;
        },
      });
    } catch (error) {
      // ALTCHA may resolve null or reject with its own error after reset().
      // Preserve the form lifecycle's AbortError contract in that race.
      signal?.throwIfAborted();
      throw error;
    }
  }

  useImperativeHandle(ref, () => ({ getCredential, reset }));

  const attachWidget = useCallback(
    (element: HTMLElement | null): (() => void) | undefined => {
      if (!element) {
        widgetRef.current = null;
        return;
      }

      let active = true;
      let cleaned = false;
      let configuredWidget: AltchaWidgetElement | null = null;
      let removeListeners = (): void => undefined;
      let removeFocusTrigger = (): void => undefined;

      const cleanup = (): void => {
        if (cleaned) return;
        cleaned = true;
        active = false;
        removeFocusTrigger();
        removeListeners();
        configuredWidget?.reset();
        controller.reset();
        payloadRef.current = null;
        autoVerifierRef.current = null;
        if (widgetRef.current === configuredWidget) {
          widgetRef.current = null;
        }
      };
      cleanupWidgetRef.current = cleanup;

      const initialize = async (): Promise<void> => {
        await customElements.whenDefined("altcha-widget");
        // ALTCHA's Svelte custom element exposes methods after connectedCallback,
        // while Preact can invoke the ref callback just before that initialization.
        await Promise.resolve();
        if (!isAltchaWidgetElement(element)) {
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve()),
          );
        }
        if (!active) return;
        if (!isAltchaWidgetElement(element)) {
          setState("error");
          return;
        }

        configuredWidget = element;
        widgetRef.current = element;
        const autoVerifier = createAltchaAutoVerifier({
          isActive: (): boolean => active && activeRef.current,
          onError: (): void => setState("error"),
          verify: (): Promise<unknown> => element.verify(),
        });
        autoVerifierRef.current = autoVerifier;
        const handleVerified = (event: Event): void => {
          if (!active) return;
          const detail = (event as CustomEvent<AltchaVerifiedDetail>).detail;
          payloadRef.current = detail.payload;
          setState("verified");
        };
        const handleStateChange = (event: Event): void => {
          if (!active) return;
          const detail = (event as CustomEvent<AltchaEventDetail>).detail;
          if (isAltchaState(detail.state)) {
            setState(detail.state);
          }
        };
        const handleExpired = (): void => {
          if (!active) return;
          payloadRef.current = null;
          // 期限切れ後は次の契機で再度自動検証できるよう一回性ガードを戻す。
          autoVerifier.release();
          setState("expired");
        };

        removeListeners = registerAltchaWidgetListeners(element, {
          verified: handleVerified,
          statechange: handleStateChange,
          expired: handleExpired,
        });
        const isCoarse = detectCoarsePointer();
        await element.configure(
          createAltchaWidgetConfiguration(
            operation,
            API_BASE,
            getAltchaChallengePath,
            controller.fetch,
            resolveAltchaWorkerConcurrency(
              navigator.hardwareConcurrency,
              isCoarse,
            ),
            hideFooterRef.current,
          ),
        );
        if (!active) return;
        if (retryWhenReadyRef.current) {
          retryWhenReadyRef.current = false;
          // 利用者が明示した再試行は常に新しい検証を始める。
          autoVerifier.start();
          void element.verify().catch(() => {
            if (active) setState("error");
          });
        } else if (isCoarse) {
          // SP: すでに active な状態で widget が configure され終わったら、
          // open 契機を1回だけここで補って verify を起動する。
          if (activeRef.current) autoVerifier.request();
        } else {
          // PC: form の focusin を購読する。1 payload = 1 投稿の運用に合わせ、
          // フォーカス契機で自動的に新 challenge を1回取りに行く。
          removeFocusTrigger = registerAltchaFocusTrigger(
            element,
            document.activeElement,
            autoVerifier.request,
          );
        }
      };

      void initialize().catch(() => {
        if (active) setState("error");
      });

      return () => {
        if (cleanupWidgetRef.current === cleanup) {
          cleanupWidgetRef.current = noop;
        }
        cleanup();
      };
    },
    [controller, operation],
  );

  return shouldRenderWidget ? (
    <AltchaWidgetView
      attachWidget={attachWidget}
      descriptionId={descriptionId}
      labelId={labelId}
      state={state}
      widgetGeneration={widgetGeneration}
      onRetry={(): void => {
        reset();
        retryWhenReadyRef.current = true;
      }}
    />
  ) : null;
});

function isAltchaWidgetElement(
  element: HTMLElement | null,
): element is AltchaWidgetElement {
  return (
    element !== null &&
    typeof Reflect.get(element, "verify") === "function" &&
    typeof Reflect.get(element, "configure") === "function"
  );
}

function isAltchaState(
  state: AltchaEventDetail["state"],
): state is AltchaState {
  return state in ALTCHA_STATUS_MESSAGES;
}
